import { useEffect, useRef, useState } from "preact/hooks";
import { replaceScript, transferring, message, script } from "./state";
import {
  EXAMPLE_JSON,
  CARD_EXAMPLE_JSON,
  MAX_IMPORT_BYTES,
  importPrompt,
} from "./prompt-format";
import { Icon } from "./icons";

export function ScriptTransfer({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [json, setJson] = useState("");
  const [error, setError] = useState("");
  const [readingFile, setReadingFile] = useState(false);
  useEffect(function () {
    const previousFocus = document.activeElement as HTMLElement | null;
    transferring.value = true;
    dialog.current?.showModal();
    return function () {
      transferring.value = false;
      dialog.current?.close();
      previousFocus?.focus();
    };
  }, []);

  async function readFile(file: File | undefined) {
    if (!file) return;
    setError("");
    if (file.size > MAX_IMPORT_BYTES) {
      setError("Choose a JSON file smaller than 256 KB.");
      return;
    }
    setReadingFile(true);
    try {
      setJson(await file.text());
    } catch {
      setError("Could not read this file. Try again or paste its JSON below.");
    } finally {
      setReadingFile(false);
    }
  }

  function applyImport(event: SubmitEvent) {
    event.preventDefault();
    try {
      const imported = importPrompt(json);
      replaceScript(imported);
      message.value = `Imported “${imported.title}” with its pace, emphasis, and pauses.`;
      onClose();
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Could not import this script.",
      );
    }
  }

  return (
    <dialog
      ref={dialog}
      class="transfer-dialog"
      aria-labelledby="transfer-title"
      onClose={onClose}
    >
      <div class="transfer-heading">
        <div>
          <h2 id="transfer-title">Import JSON</h2>
        </div>
        <button
          type="button"
          class="icon-button"
          aria-label="Close JSON tools"
          onClick={onClose}
        >
          <Icon name="close" />
        </button>
      </div>
      <form class="transfer-content" onSubmit={applyImport}>
        <p class="transfer-description">
          Paste the JSON from your LLM or open a file. Each cue can be a word or
          passage, with its own pace, emphasis, and pause.
        </p>
        <div class="file-actions">
          <input
            ref={fileInput}
            type="file"
            accept=".json,application/json"
            aria-label="Choose JSON file"
            onChange={function (event) {
              void readFile(event.currentTarget.files?.[0]);
              event.currentTarget.value = "";
            }}
          />
          <button
            type="button"
            class="outline-button"
            disabled={readingFile}
            onClick={function () {
              fileInput.current?.click();
            }}
          >
            {readingFile ? "Reading…" : "Open JSON file"}
          </button>
          <button
            type="button"
            class="text-button"
            disabled={readingFile}
            onClick={function () {
              setJson(
                script.value.mode === "cue-cards"
                  ? CARD_EXAMPLE_JSON
                  : EXAMPLE_JSON,
              );
              setError("");
            }}
          >
            Use example
          </button>
        </div>
        <label for="prompt-json">Script JSON</label>
        <textarea
          id="prompt-json"
          class="json-input"
          placeholder={
            '{\n  "version": 1,\n  "title": "Your script",\n  "cues": […]\n}'
          }
          value={json}
          onInput={function (event) {
            setJson(event.currentTarget.value);
            setError("");
          }}
          spellcheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          aria-invalid={!!error}
          aria-describedby={error ? "import-error" : "import-note"}
        />
        <p id="import-note" class="import-note">
          Import replaces the current script after validation. Nothing is sent
          to an LLM by this app.
        </p>
        {error && (
          <p id="import-error" class="import-error" role="alert">
            {error}
          </p>
        )}
        <div class="transfer-footer">
          <button type="button" class="quiet" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            class="primary"
            disabled={!json.trim() || readingFile}
          >
            Import script
          </button>
        </div>
      </form>
    </dialog>
  );
}
